﻿import {useEffect, useState} from "react";
import {useLocationSearchParams} from "./useLocationSearchParams";
import {getGeocodedPlaceFromLocalStorage} from "../../util/PlaceStorageUtil";
import GeocoderResult = google.maps.GeocoderResult;


interface GeocodedPlace {
  // The reconstructed place from localStorage, or undefined if not found or invalid
  geocodedPlaceByLocalStorageLatLng: GeocoderResult | undefined,
  // True if retrieval was attempted but failed; false if successful or not attempted
  failedToGetByLocalStorage: boolean
}
/**
 * Custom React hook that conditionally retrieves and reconstructs a geocoded place from browser localStorage.
 *
 * The hook attempts to retrieve a previously saved location from localStorage when:
 * - No place is currently set (`hasCurrentPlace === false`)
 * - The caller signals it should attempt retrieval (`shouldTryLocalStorage === true`)
 * - Current URL location parameters don't provide a valid city/state
 *
 * **Success case:** localStorage contains valid geocoded place data → returns reconstructed `GeocoderResult`
 * **Failure case:** localStorage is empty, corrupted, or invalid → returns undefined and sets failure flag
 *
 * The failure flag allows callers to chain attempt logic (e.g., try next data source on failure).
 * The hook automatically resets its failure flag whenever the current location parameters change.
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.hasCurrentPlace - If true, hook short-circuits and does nothing (place already loaded)
 * @param {boolean} params.shouldTryLocalStorage - If false, hook short-circuits and does nothing (caller signals not to attempt)
 *
 * @returns {GeocodedPlace} result - Hook result object
 * ```ts
 * {
 *  geocodedPlaceByLocalStorageLatLng: GeocoderResult | undefined, // The geocoded place reconstructed from localStorage, or undefined if retrieval failed
 *  failedToGetByLocalStorage: boolean // True if retrieval was attempted but failed; false if successful or not attempted
 * }
 * ```
 *
 * @example
 * const {
 *   geocodedPlaceByLocalStorageLatLng,
 *   failedToGetByLocalStorage
 * } = useGeocodedPlaceByLocalStorageLatLng({
 *   hasCurrentPlace: false,
 *   shouldTryLocalStorage: true
 * });
 *
 * @see {@link getGeocodedPlaceFromLocalStorage} - Utility that deserializes and validates localStorage data
 * @see {@link useLocationSearchParams} - Hook for accessing current location parameters from URL
 */
export const useGeocodedPlaceByLocalStorageLatLng = ({hasCurrentPlace, shouldTryLocalStorage}: {
  hasCurrentPlace: boolean,
  shouldTryLocalStorage: boolean
}): GeocodedPlace => {
  // Tracks whether localStorage retrieval attempt was made and failed
  const [failedToGetByLocalStorage, setFailedToGetByLocalStorage] = useState<boolean>(false);

  // Current location parameters from URL (used to verify if caller should skip localStorage retrieval)
  const [currentLocationParams] = useLocationSearchParams();

  // Geocoded place data reconstructed from localStorage (if retrieval succeeded)
  const [geocodedPlace, setGeocodedPlace] = useState<GeocoderResult | undefined>(undefined);

  // Reset failure flag whenever location parameters change (indicates new search attempt)
  useEffect(() => {
    if (currentLocationParams) {
      setFailedToGetByLocalStorage(false);
    }
  }, [currentLocationParams]);

  // Reset failure flag when geocoded place is successfully retrieved
  useEffect(() => {
    if (geocodedPlace) {
      setFailedToGetByLocalStorage(false);
    }
  }, [geocodedPlace]);

  /**
   * Main effect: Attempts to retrieve geocoded place from localStorage when conditions are met.
   * Runs when:
   * - hasCurrentPlace is false (no place currently loaded)
   * - shouldTryLocalStorage is true (caller signals to attempt retrieval)
   * - Current location parameters lack city/state (to avoid redundant retrieval)
   */
  useEffect(() => {
    if (!hasCurrentPlace && shouldTryLocalStorage && (!currentLocationParams?.city || !currentLocationParams?.state)) {
      const reconstructedPlace = getGeocodedPlaceFromLocalStorage();

      if (reconstructedPlace) {
        // Retrieval succeeded: valid place data was found and reconstructed
        setGeocodedPlace(reconstructedPlace);
        setFailedToGetByLocalStorage(false);
      } else {
        // Retrieval failed: localStorage was empty, corrupted, or contained invalid data
        setGeocodedPlace(undefined);
        setFailedToGetByLocalStorage(true);
      }
    }
  }, [currentLocationParams, hasCurrentPlace, shouldTryLocalStorage]);

  return {
    geocodedPlaceByLocalStorageLatLng: geocodedPlace,
    failedToGetByLocalStorage
  }
}
